import * as React from 'react';
import { cn } from '@/lib/utils';

const alertVariants = {
  default: 'bg-white text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100',
  destructive:
    'border-red-500/50 text-red-600 dark:border-red-500 dark:text-red-400 [&>svg]:text-red-600 dark:[&>svg]:text-red-400',
  warning:
    'border-amber-500/50 text-amber-600 dark:border-amber-500 dark:text-amber-400 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400',
  success:
    'border-green-500/50 text-green-600 dark:border-green-500 dark:text-green-400 [&>svg]:text-green-600 dark:[&>svg]:text-green-400',
};

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: keyof typeof alertVariants;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'relative w-full rounded-lg border border-neutral-200 p-4 dark:border-neutral-700 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-neutral-900 dark:[&>svg]:text-neutral-100 [&>svg~*]:pl-7',
        alertVariants[variant],
        className
      )}
      {...props}
    />
  )
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
